import { useEffect, useRef } from 'react';
import { API_CONFIG } from '@/config/apiConfig';
import { apiService } from '@/services/apiService';
import { GAMIFY_HOST_CLASS, mountGamifyWidget, type GamifyWidgetApi } from './gamifyWidgetHost';

declare global {
  interface Window {
    GamifyWidget?: GamifyWidgetApi;
  }
}

const CDN = {
  REACT_18: 'https://unpkg.com/react@18/umd/react.production.min.js',
  REACT_DOM_18: 'https://unpkg.com/react-dom@18/umd/react-dom.production.min.js',
  WIDGET_CSS: 'https://gamify-widget.pages.dev/gamify-widget.css',
  WIDGET_JS: 'https://gamify-widget.pages.dev/gamify-widget.umd.js',
};

function loadScript(src: string, crossorigin = false): Promise<void> {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = src;
    script.async = false;
    if (crossorigin) script.setAttribute('crossorigin', '');
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
    document.head.appendChild(script);
  });
}

function loadStylesheet(href: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`link[href="${href}"]`)) {
      resolve();
      return;
    }
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    link.onload = () => resolve();
    link.onerror = () => reject(new Error(`Failed to load stylesheet: ${href}`));
    document.head.appendChild(link);
  });
}

/**
 * GamifyEngine widget — mounts when `VITE_GAMIFY_WIDGET_ENABLED=true`, skipped otherwise.
 * Load React 18 + ReactDOM + widget CSS/JS from CDN, then call GamifyWidget.mount().
 */
export default function GamifyWidgetMount() {
  const mountedRef = useRef(false);
  const hostRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (
      import.meta.env.VITE_GAMIFY_WIDGET_ENABLED !== 'true' ||
      mountedRef.current ||
      typeof window === 'undefined'
    ) {
      return;
    }

    const { API_BASE_URL, TENANT_SLUG, FAB_POSITION } = API_CONFIG.GAMIFY;

    const mount = () => {
      const host = hostRef.current;
      if (!window.GamifyWidget?.mount || mountedRef.current || !host) return;
      mountedRef.current = true;
      mountGamifyWidget(window.GamifyWidget, host, {
        apiBaseUrl: API_BASE_URL,
        tenantSlug: TENANT_SLUG,
        getUserToken: () => Promise.resolve(apiService.getAccessToken() ?? ''),
        fabPosition: FAB_POSITION,
      });
    };

    if (window.GamifyWidget?.mount) {
      mount();
      return;
    }

    // Widget UMD bundle (or jsx-runtime) needs process.env in browser — polyfill before load.
    if (typeof (window as unknown as { process?: unknown }).process === 'undefined') {
      (window as unknown as { process: { env: { NODE_ENV: string } } }).process = {
        env: { NODE_ENV: import.meta.env.DEV ? 'development' : 'production' },
      };
    }

    let cancelled = false;
    (async () => {
      try {
        await loadScript(CDN.REACT_18, true);
        if (cancelled) return;
        await loadScript(CDN.REACT_DOM_18, true);
        if (cancelled) return;
        await loadStylesheet(CDN.WIDGET_CSS);
        if (cancelled) return;
        await loadScript(CDN.WIDGET_JS);
        if (cancelled) return;
        mount();
      } catch (e) {
        if (import.meta.env.DEV) {
          console.warn('[GamifyWidget] Load failed:', e);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return <div ref={hostRef} className={GAMIFY_HOST_CLASS} data-gamify-host="" />;
}
